import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import TopNav from "../../commonUI/TopHeader/TopHeader";
import { getUserRecordsCount, getUsersClients } from "../../../services/adminService";
import { modal, alert } from "../../../js/modules/Dialogs";
import { useLoader } from "../../../hooks/useLoader";
import { sessionExpired } from "../../../config/messages";
import { toScreen } from "../../../js/modules/Routing";
import Pagination from "../../commonUI/Pagination/Pagination";
import SearchFilterBar from "../../commonUI/SearchFilterBar/SearchFilterBar";
import DataTable from "../../commonUI/DataTable/DataTable";
import { MessageButtonIcon } from "../../commonUI/Icons/SvgIcon";
import DateTime from "../../commonUI/DateTime/DateTime";

const Clients = ({ userDetails }) => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const [searchValue, setSearchValue] = useState(null);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(1);
  const [filter, setFilter] = useState(
    window.history.state?.usr?.selectedFilter ?? "active"
  );
  const [sort, setSort] = useState("email=-1");
  const [activeTab, setActiveTab] = useState("active");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    active: null,
    blocked: null,
    deleted: null,
  });
  const [clients, setClients] = useState([]);
  const [filterCount, setFilterCount] = useState({
    activeUsers: 0,
    blockedUsers: 0,
    deletedUsers: 0,
    totalDocs: 0,
  });

  useEffect(() => {
    setInputValue(page);
  }, [page]);

  useEffect(() => {
    getClients();
  }, [filter, page, pageSize, sort, search, activeFilters[activeTab]]);

  useEffect(() => {
    fetchFilterCount();
  }, []);

  const getClients = async (event) => {
    if (event) event.preventDefault();
    showLoader();
    const queryParams = {
      filter,
      search,
      page,
      limit: pageSize,
      sort,
      ...(activeFilters[activeTab] && { customFilter: activeFilters[activeTab] }),
    };
    try {
      const resJson = await getUsersClients(queryParams);
      hideLoader();
      setTotalPages(Math.ceil(resJson?.totalRecords / pageSize));
      setClients(resJson?.data || []);
    } catch (error) {
      hideLoader();
      if (error.response?.status === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        setClients([]);
        alert(error.message || "Failed to fetch clients");
      }
    }
  };

  const fetchFilterCount = async () => {
    showLoader();
    try {
      const resJson = await getUserRecordsCount({ userType: "user" });
      hideLoader();
      setFilterCount({
        activeUsers: resJson?.activeUsers,
        blockedUsers: resJson?.blockedUsers,
        deletedUsers: resJson?.deletedUsers,
        totalDocs: resJson?.totalDocs,
      });
    } catch (error) {
      hideLoader();
      if (error.response?.status === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        alert(error.message || "Failed to fetch filter count");
      }
    }
  };

  const searchClient = () => {
    setSearch(searchValue);
    setFilter(searchValue === "" ? "active" : null);
    setPage(1);
  };

  const sortClients = (event, field) => {
    if (event) event.preventDefault();
    const sortFields = {
      name: { asc: "name=1", desc: "name=-1" },
      email: { asc: "email=1", desc: "email=-1" },
      phone: { asc: "phone=1", desc: "phone=-1" },
      jobs: { asc: "jobs=1", desc: "jobs=-1" },
      completed: { asc: "completed=1", desc: "completed=-1" },
      registeredOn: { asc: "registeredOn=1", desc: "registeredOn=-1" },
      blockedReason: { asc: "blockedReason=1", desc: "blockedReason=-1" },
      blockedOn: { asc: "blockedOn=1", desc: "blockedOn=-1" },
      deletedReason: { asc: "deletedReason=1", desc: "deletedReason=-1" },
      deletedOn: { asc: "deletedOn=1", desc: "deletedOn=-1" },
      completedJobCount: { asc: "completedJobCount=1", desc: "completedJobCount=-1" },
      totalJobCount: { asc: "totalJobCount=1", desc: "totalJobCount=-1" },
    };
    const currentSort = sortFields[field];
    if (!currentSort) return;
    setSearch("");
    setSort(sort === currentSort.asc ? currentSort.desc : currentSort.asc);
    setPage(1);
  };

  const getSortClass = (key) => {
    if (sort === `${key}=1`) return "sorting-icon sorting-icon-up";
    if (sort === `${key}=-1`) return "sorting-icon sorting-icon-down";
    return "";
  };

  const handleReset = () => {
    setActiveTab("active");
    setSort("email=-1");
    setSearchValue(null);
    setSearch("");
    setStartDate(null);
    setEndDate(null);
    setActiveFilters({ active: null, blocked: null, deleted: null });
    setFilter("active");
    setPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setFilter(tab);
  };

  const SortButton = useCallback(
    ({ field, label }) => (
      <button onClick={(e) => sortClients(e, field)} className="sort-button">
        {label}
        {sort.includes(`${field}=`) && <span className={getSortClass(field)} />}
      </button>
    ),
    [sort]
  );

  const renderClientRow = (client, index) => (
    <tr key={client?._id || index}>
      <td>{client?.accountInfo?.firstname}</td>
      <td>{client?.accountInfo?.telephone}</td>
      <td>
        <a href={`mailto:${client?.accountInfo?.email}`}>
          {client?.accountInfo?.email}
        </a>
      </td>
      <td>{client?.totalJobCount ?? 0}</td>
      <td>{client?.completedJobs ?? 0}</td>
      <td>
        <DateTime date={client?.registeredOn} />
      </td>

      {activeTab === "blocked" && (
        <>
          <td>{client?.blockedReason || "-"}</td>
          <td>
            <DateTime date={client?.blockedOn} />
          </td>
        </>
      )}

      {activeTab === "deleted" && (
        <>
          <td>{client?.deletedReason || "-"}</td>
          <td>
            <DateTime date={client?.deletedOn} />
          </td>
        </>
      )}

      <td>
        <div className="d-flex align-items-center g-8 justify-content-end">
          {activeTab !== "deleted" && (
            <button
              type="button"
              className="custom-btn"
              onClick={(e) =>
                toScreen(e, navigate, `/message/${client.shortId}`, false, {
                  receiverId: client?._id,
                  receiverShortId: client?.shortId,
                  receiverType: client?.userType,
                  userShortId1: "admin",
                  userShortId2: client?.shortId,
                  username2: client?.accountInfo?.username,
                  selectedPage: page,
                  selectedFilter: filter,
                  previousPath: window.location.pathname,
                })
              }
            >
              <MessageButtonIcon />
            </button>
          )}
          <button
            type="button"
            className="custom-btn"
            onClick={(e) => toScreen(e, navigate, `/client/${client?.shortId}`)}
          >
            View
          </button>
        </div>
      </td>
    </tr>
  );

  const tabConfig = useMemo(
    () => ({
      active: {
        colSpan: 7,
        renderHeader: () => (
          <>
            <th><SortButton field="name" label="Client" /></th>
            <th><SortButton field="phone" label="Phone" /></th>
            <th><SortButton field="email" label="Email" /></th>
            <th><SortButton field="totalJobCount" label="Jobs" /></th>
            <th><SortButton field="completedJobCount" label="Completed" /></th>
            <th><SortButton field="registeredOn" label="Registered On" /></th>
            <th></th>
          </>
        ),
      },
      blocked: {
        colSpan: 9,
        renderHeader: () => (
          <>
            <th><SortButton field="name" label="Client" /></th>
            <th><SortButton field="phone" label="Phone" /></th>
            <th><SortButton field="email" label="Email" /></th>
            <th><SortButton field="jobs" label="Jobs" /></th>
            <th><SortButton field="completed" label="Completed" /></th>
            <th><SortButton field="registeredOn" label="Registered On" /></th>
            <th><SortButton field="blockedReason" label="Blocked Reason" /></th>
            <th><SortButton field="blockedOn" label="Blocked On" /></th>
            <th></th>
          </>
        ),
      },
      deleted: {
        colSpan: 9,
        renderHeader: () => (
          <>
            <th><SortButton field="name" label="Client" /></th>
            <th><SortButton field="phone" label="Phone" /></th>
            <th><SortButton field="email" label="Email" /></th>
            <th><SortButton field="jobs" label="Jobs" /></th>
            <th><SortButton field="completed" label="Completed" /></th>
            <th><SortButton field="registeredOn" label="Registered On" /></th>
            <th><SortButton field="deletedReason" label="Deleted Reason" /></th>
            <th><SortButton field="deletedOn" label="Deleted On" /></th>
            <th></th>
          </>
        ),
      },
    }),
    [SortButton]
  );

  return (
    <>
      <TopNav title="Clients" />

      <div className="global-body">
        <div className="global-body-container">
          <div className="custom-tabs mt-0">
            <div
              className={`tab-item ${activeTab === "active" ? "active" : ""}`}
              onClick={() => handleTabChange("active")}
            >
              Active <span>({filterCount?.activeUsers || 0})</span>
            </div>
            <div
              className={`tab-item ${activeTab === "blocked" ? "active" : ""}`}
              onClick={() => handleTabChange("blocked")}
            >
              Blocked <span>({filterCount?.blockedUsers || 0})</span>
            </div>
            <div
              className={`tab-item ${activeTab === "deleted" ? "active" : ""}`}
              onClick={() => handleTabChange("deleted")}
            >
              Deleted <span>({filterCount?.deletedUsers || 0})</span>
            </div>
          </div>

          <SearchFilterBar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onSearchSubmit={searchClient}
            searchPlaceholder="Search..."
            filters={[
              {
                type: "dateRange",
                key: "registeredOn",
                label: "Registered on",
                value: { start: startDate, end: endDate },
                onChange: ({ start, end }) => {
                  setStartDate(start);
                  setEndDate(end);
                },
                placeholder: "Anytime",
              },
            ]}
            onReset={handleReset}
          />

          <DataTable
            data={clients}
            columns={[]}
            renderRow={renderClientRow}
            onSort={sortClients}
            getSortClass={getSortClass}
            renderHeader={tabConfig[activeTab]?.renderHeader}
            emptyMessage="No clients found"
            tableClassName="table clientTable"
            colSpan={tabConfig[activeTab]?.colSpan}
          />

          <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </>
  );
};

Clients.propTypes = {
  userDetails: PropTypes.object.isRequired,
  updateState: PropTypes.func.isRequired,
};

export default Clients;
